import { useEffect, useRef } from 'react';

const CHECK_THROTTLE_MS = 30_000;
const OVERLAY_DURATION_MS = 800;

let initialVersion: string | null = null;
let isReloading = false;
let lastCheckTimestamp = 0;

async function fetchVersion(): Promise<string | null> {
  try {
    const resp = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    return data.v ?? null;
  } catch {
    return null;
  }
}

function showOverlayAndReload(): void {
  if (isReloading) return;
  isReloading = true;

  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position:fixed;top:0;left:0;right:0;bottom:0;z-index:99999;
    background:rgba(255,255,255,0.95);
    display:flex;align-items:center;justify-content:center;flex-direction:column;
    font-family:system-ui,-apple-system,sans-serif;
  `;
  overlay.innerHTML = `
    <div style="width:40px;height:40px;border:3px solid #e5e7eb;border-top-color:#2563eb;border-radius:50%;animation:spin 0.8s linear infinite"></div>
    <p style="margin-top:16px;color:#374151;font-size:14px;">Actualizando...</p>
    <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
  `;
  document.body.appendChild(overlay);

  setTimeout(async () => {
    try {
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(n => caches.delete(n)));
      }
    } catch { /* ignore */ }
    window.location.reload();
  }, OVERLAY_DURATION_MS);
}

async function checkAndUpdate(): Promise<void> {
  if (isReloading) return;

  const now = Date.now();
  if (now - lastCheckTimestamp < CHECK_THROTTLE_MS) return;
  lastCheckTimestamp = now;

  const serverVersion = await fetchVersion();
  if (!serverVersion) return;

  if (initialVersion === null) {
    initialVersion = serverVersion;
    return;
  }

  if (serverVersion !== initialVersion) {
    showOverlayAndReload();
  }
}

export function safeReload(): void {
  if (isReloading) return;
  showOverlayAndReload();
}

export function useAppUpdater(): void {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    checkAndUpdate();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndUpdate();
      }
    };

    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        checkAndUpdate();
      }
    };

    intervalRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkAndUpdate();
      }
    }, 2 * 60 * 1000);

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);
}
